import { ActionContext } from "vuex";
import store from "@/store/store";
import { putResistances } from "@/common/character/resistances";
import {
  deleteStatusEffects,
  putStatusEffects,
} from "@/common/character/status";
import { putDamageReduction } from "@/common/character/damageReductions";

export const resistance = {
  vulnerable: { value: 4, displayName: "Vulnerable" },
  susceptible: { value: 2, displayName: "Susceptible" },
  normal: { value: 1, displayName: "Normal" },
  resistant: { value: 5, displayName: "Resistant" },
  immune: { value: 0, displayName: "Immune" },
};

export interface ResistanceStore {
  id: number;
  resistLevel: { displayName: string; value: number };
}

export interface StatusStore {
  // Number is equivalent to how many ranks of DR they have
  focused: boolean;
  focused2: boolean;
  focused3: boolean;
  focused4: boolean;
  damageReduction: {
    mundane: { level: number; id: number };
    blunt: { level: number; id: number };
    edged: { level: number; id: number };
    piercing: { level: number; id: number };
    stress: { level: number; id: number };
    toxic: { level: number; id: number };
    elemental: { level: number; id: number };
    fire: { level: number; id: number };
    ice: { level: number; id: number };
    lightning: { level: number; id: number };
    force: { level: number; id: number };
    magicDamage: { level: number; id: number };
    blight: { level: number; id: number };
    divine: { level: number; id: number };
    pureMagic: { level: number; id: number };
  };
  resistances: {
    mundane: ResistanceStore;
    blunt: ResistanceStore;
    edged: ResistanceStore;
    piercing: ResistanceStore;
    stress: ResistanceStore;
    toxic: ResistanceStore;
    elemental: ResistanceStore;
    fire: ResistanceStore;
    ice: ResistanceStore;
    lightning: ResistanceStore;
    force: ResistanceStore;
    magicDamage: ResistanceStore;
    blight: ResistanceStore;
    divine: ResistanceStore;
    pureMagic: ResistanceStore;
  };
  statusEffects: {
    blinded: { active: boolean; check: number; id: number };
    befuddled: { active: boolean; check: number; id: number };
    charmed: { active: boolean; check: number; id: number };
    confused: { active: boolean; check: number; id: number };
    ethereal: { active: boolean; check: number; id: number };
    encumbered: { active: boolean; check: number; id: number };
    fatigued: { active: boolean; check: number; id: number };
    frightened: { active: boolean; check: number; id: number };
    grappled: { active: boolean; check: number; id: number };
    invisible: { active: boolean; check: number; id: number };
    paralyzed: { active: boolean; check: number; id: number };
    petrified: { active: boolean; check: number; id: number };
    pinned: { active: boolean; check: number; id: number };
    prone: { active: boolean; check: number; id: number };
    sickened: { active: boolean; check: number; id: number };
    slowed: { active: boolean; check: number; id: number };
    spellJammed: { active: boolean; check: number; id: number };
    stunned: { active: boolean; check: number; id: number };
    suffering: { active: boolean; check: number; id: number };
    unconscious: { active: boolean; check: number; id: number };
    warped: { active: boolean; check: number; id: number };
  };
}

export default {
  namespaced: true,
  state: (): StatusStore => ({
    // Number is equivalent to how many ranks of DR they have
    focused: false,
    focused2: false,
    focused3: false,
    focused4: false,
    damageReduction: {
      mundane: { level: 0, id: 0 },
      blunt: { level: 0, id: 0 },
      edged: { level: 0, id: 0 },
      piercing: { level: 0, id: 0 },
      stress: { level: 0, id: 0 },
      toxic: { level: 0, id: 0 },
      elemental: { level: 0, id: 0 },
      fire: { level: 0, id: 0 },
      ice: { level: 0, id: 0 },
      lightning: { level: 0, id: 0 },
      force: { level: 0, id: 0 },
      magicDamage: { level: 0, id: 0 },
      blight: { level: 0, id: 0 },
      divine: { level: 0, id: 0 },
      pureMagic: { level: 0, id: 0 },
    },
    resistances: {
      mundane: { resistLevel: resistance.normal, id: 0 },
      blunt: { resistLevel: resistance.normal, id: 0 },
      edged: { resistLevel: resistance.normal, id: 0 },
      piercing: { resistLevel: resistance.normal, id: 0 },
      stress: { resistLevel: resistance.normal, id: 0 },
      toxic: { resistLevel: resistance.normal, id: 0 },
      elemental: { resistLevel: resistance.normal, id: 0 },
      fire: { resistLevel: resistance.normal, id: 0 },
      ice: { resistLevel: resistance.normal, id: 0 },
      lightning: { resistLevel: resistance.normal, id: 0 },
      force: { resistLevel: resistance.normal, id: 0 },
      magicDamage: { resistLevel: resistance.normal, id: 0 },
      blight: { resistLevel: resistance.normal, id: 0 },
      divine: { resistLevel: resistance.normal, id: 0 },
      pureMagic: { resistLevel: resistance.normal, id: 0 },
    },
    statusEffects: {
      blinded: { active: false, check: 0, id: 0 },
      befuddled: { active: false, check: 0, id: 0 },
      charmed: { active: false, check: 0, id: 0 },
      confused: { active: false, check: 0, id: 0 },
      ethereal: { active: false, check: 0, id: 0 },
      encumbered: { active: false, check: 0, id: 0 },
      fatigued: { active: false, check: 0, id: 0 },
      frightened: { active: false, check: 0, id: 0 },
      grappled: { active: false, check: 0, id: 0 },
      invisible: { active: false, check: 0, id: 0 },
      paralyzed: { active: false, check: 0, id: 0 },
      petrified: { active: false, check: 0, id: 0 },
      pinned: { active: false, check: 0, id: 0 },
      prone: { active: false, check: 0, id: 0 },
      sickened: { active: false, check: 0, id: 0 },
      slowed: { active: false, check: 0, id: 0 },
      spellJammed: { active: false, check: 0, id: 0 },
      stunned: { active: false, check: 0, id: 0 },
      suffering: { active: false, check: 0, id: 0 },
      unconscious: { active: false, check: 0, id: 0 },
      warped: { active: false, check: 0, id: 0 },
    },
  }),
  getters: {
    damageReduction: (state: StatusStore) => {
      return state.damageReduction;
    },
    resistances: (state: StatusStore) => {
      return state.resistances;
    },
    statusEffects: (state: StatusStore) => {
      return state.statusEffects;
    },
    status: (state: StatusStore) => {
      return {
        damageReduction: state.damageReduction,
        resistances: state.resistances,
        statusEffects: state.statusEffects,
      };
    },
  },
  mutations: {
    setDamageReduction: (
      state: StatusStore,
      newData: {
        mundane: { level: number; id: number };
        blunt: { level: number; id: number };
        edged: { level: number; id: number };
        piercing: { level: number; id: number };
        stress: { level: number; id: number };
        toxic: { level: number; id: number };
        elemental: { level: number; id: number };
        fire: { level: number; id: number };
        ice: { level: number; id: number };
        lightning: { level: number; id: number };
        force: { level: number; id: number };
        magicDamage: { level: number; id: number };
        blight: { level: number; id: number };
        divine: { level: number; id: number };
        pureMagic: { level: number; id: number };
      }
    ) => {
      state.damageReduction = newData;
    },
    setResistances: (
      state: StatusStore,
      newData: {
        mundane: ResistanceStore;
        blunt: ResistanceStore;
        edged: ResistanceStore;
        piercing: ResistanceStore;
        stress: ResistanceStore;
        toxic: ResistanceStore;
        elemental: ResistanceStore;
        fire: ResistanceStore;
        ice: ResistanceStore;
        lightning: ResistanceStore;
        force: ResistanceStore;
        magicDamage: ResistanceStore;
        blight: ResistanceStore;
        divine: ResistanceStore;
        pureMagic: ResistanceStore;
      }
    ) => {
      state.resistances = newData;
    },
    setStatusEffects: (
      state: StatusStore,
      newData: {
        blinded: { active: boolean; check: number; id: number };
        befuddled: { active: boolean; check: number; id: number };
        charmed: { active: boolean; check: number; id: number };
        confused: { active: boolean; check: number; id: number };
        ethereal: { active: boolean; check: number; id: number };
        encumbered: { active: boolean; check: number; id: number };
        fatigued: { active: boolean; check: number; id: number };
        frightened: { active: boolean; check: number; id: number };
        grappled: { active: boolean; check: number; id: number };
        invisible: { active: boolean; check: number; id: number };
        paralyzed: { active: boolean; check: number; id: number };
        petrified: { active: boolean; check: number; id: number };
        pinned: { active: boolean; check: number; id: number };
        prone: { active: boolean; check: number; id: number };
        sickened: { active: boolean; check: number; id: number };
        slowed: { active: boolean; check: number; id: number };
        spellJammed: { active: boolean; check: number; id: number };
        stunned: { active: boolean; check: number; id: number };
        suffering: { active: boolean; check: number; id: number };
        unconscious: { active: boolean; check: number; id: number };
        warped: { active: boolean; check: number; id: number };
      }
    ) => {
      state.statusEffects = newData;
    },
    switchFocused: (state: StatusStore) => {
      state.focused = !state.focused;
    },
    switchFocused2: (state: StatusStore) => {
      state.focused2 = !state.focused2;
    },
    switchFocused3: (state: StatusStore) => {
      state.focused3 = !state.focused3;
    },
    switchFocused4: (state: StatusStore) => {
      state.focused4 = !state.focused4;
    },
    setStatusEffect: (
      state: StatusStore,
      newData: { effect: string; active: any; check: number }
    ) => {
      if (newData.effect === "spell jammed") {
        newData.effect = "spellJammed";
      }
      state.statusEffects[
        newData.effect as keyof StatusStore["statusEffects"]
      ].active = newData.active;
      state.statusEffects[
        newData.effect as keyof StatusStore["statusEffects"]
      ].check = newData.check;
    },
    setStatusEffectFromDb: (
      state: StatusStore,
      newData: { name: string; check: number; id: number }
    ) => {
      if (newData.name === "spell jammed") {
        newData.name = "spellJammed";
      }
      state.statusEffects[
        newData.name as keyof StatusStore["statusEffects"]
      ].active = true;
      state.statusEffects[
        newData.name as keyof StatusStore["statusEffects"]
      ].check = newData.check;
      if (newData.id && newData.id > 0) {
        state.statusEffects[
          newData.name as keyof StatusStore["statusEffects"]
        ].id = newData.id;
      }
    },
    setResistanceFromDb: (
      state: StatusStore,
      newData: { name: string; level: string; id: number }
    ) => {
      let resistLevel;
      switch (parseInt(newData.level)) {
        case 0.5:
          resistLevel = resistance.resistant;
          break;
        case 0:
          resistLevel = resistance.immune;
          break;
        case 2:
          resistLevel = resistance.susceptible;
          break;
        case 4:
          resistLevel = resistance.vulnerable;
          break;
        default:
          resistLevel = resistance.normal;
          break;
      }
      if (newData.id && newData.id > 0) {
        state.resistances[newData.name as keyof StatusStore["resistances"]].id =
          newData.id;
      }
      state.resistances[
        newData.name as keyof StatusStore["resistances"]
      ].resistLevel = resistLevel;
    },
    setDamageReductionFromDb: (
      state: StatusStore,
      newData: { name: string; level: number; id: number }
    ) => {
      state.damageReduction[
        newData.name as keyof StatusStore["damageReduction"]
      ].level = newData.level;
      if (newData.id && newData.id > 0) {
        state.damageReduction[
          newData.name as keyof StatusStore["damageReduction"]
        ].id = newData.id;
      }
    },
    clearStatus: (state: StatusStore) => {
      const arr = Object.keys(state.statusEffects);
      for (const status of arr) {
        state.statusEffects[
          status as keyof StatusStore["statusEffects"]
        ].active = false;
      }
    },
    clearDr: (state: StatusStore) => {
      const arr = Object.keys(state.damageReduction);
      for (const dr of arr) {
        state.damageReduction[
          dr as keyof StatusStore["damageReduction"]
        ].level = 0;
      }
    },
    clearResist: (state: StatusStore) => {
      const arr = Object.keys(state.resistances);
      for (const resist of arr) {
        state.resistances[
          resist as keyof StatusStore["resistances"]
        ].resistLevel = resistance.normal;
      }
    },
  },
  actions: {
    putResistances: (
      context: any,
      newData: {
        resistances: {
          mundane: ResistanceStore;
          blunt: ResistanceStore;
          edged: ResistanceStore;
          piercing: ResistanceStore;
          stress: ResistanceStore;
          toxic: ResistanceStore;
          elemental: ResistanceStore;
          fire: ResistanceStore;
          ice: ResistanceStore;
          lightning: ResistanceStore;
          force: ResistanceStore;
          magicDamage: ResistanceStore;
          blight: ResistanceStore;
          divine: ResistanceStore;
          pureMagic: ResistanceStore;
        };
        character_id: string;
      }
    ) => {
      context.commit("setResistances", newData.resistances);
      const push = [];
      for (const object in context.getters.resistances) {
        const item = {
          characterId: newData.character_id,
          name: object,
          level: context.getters.resistances[object].resistLevel.value,
          id: context.getters.resistances[object].id,
        };
        push.push(item);
      }
      putResistances(newData.character_id, push);
    },
    putResistance: (
      context: any,
      newData: {
        name: string;
        level: number;
        character_id: string;
        id: number;
      }
    ) => {
      putResistances(newData.character_id, [
        {
          name: newData.name,
          level: newData.level,
          id: newData.id,
        },
      ]).then((response) => {
        context.commit("setResistanceFromDb", {
          name: response.data[0].name,
          level: response.data[0].level,
          id: response.data[0].id,
        });
      });
    },
    putDr: (
      context: any,
      newData: {
        name: string;
        level: number;
        character_id: string;
        id: number;
      }
    ) => {
      console.log(newData);
      putDamageReduction(
        newData.character_id,
        newData.name,
        newData.level,
        newData.id
      ).then((response) => {
        context.commit("setDamageReductionFromDb", {
          name: response.data[0].name,
          level: response.data[0].level,
          id: response.data[0].id,
        });
      });
    },
    updateStatus: (
      context: any,
      newData: {
        status: { active: boolean; check: number; effect: string; id: number };
        character_id: string;
      }
    ) => {
      const item = {
        characterId: newData.character_id,
        name: newData.status.effect,
        check: newData.status.check,
        id: newData.status.id,
      };
      if (newData.status.effect === "spell jammed") {
        newData.status.effect = "spellJammed";
      }

      if (newData.status.active) {
        putStatusEffects(newData.character_id, [item]).then((response) => {
          console.log(response.data[0]);
          context.commit("setStatusEffectFromDb", response.data[0]);
        });
      } else {
        context.commit("setStatusEffect", newData.status);
        deleteStatusEffects(newData.character_id, [item]);
      }
    },
  },
};
